import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { useLookupCadastre } from '@/lib/api/cadastre.ts';
import useSnackbar from '@/hooks/useSnackbar.tsx';
import { CadastrePropertySummary, CadastreRawData } from '@inversor-hub/types';

interface CadastreToolCardProps {
  title?: string;
  description?: string;
  showAsCard?: boolean;
}

const CadastreToolCard = ({
  title = 'Consulta del Catastro',
  description = 'Introduce una referencia catastral para obtener información detallada del inmueble',
  showAsCard = true,
}: CadastreToolCardProps) => {
  const [reference, setReference] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    mutate: fetchCadastre,
    data: cadastralData,
    reset: resetCadastre,
    isPending: isCadastralLoading,
  } = useLookupCadastre();
  const { show: showSnackbar } = useSnackbar();

  const handleFetchCadastreData = () => {
    const trimmedReference = reference.trim();

    if (!trimmedReference) {
      showSnackbar({
        title: 'Referencia catastral requerida',
        message: 'Introduce una referencia catastral antes de consultar.',
        variant: 'error',
      });
      return;
    }

    fetchCadastre(trimmedReference, {
      onSuccess: () => {
        setIsDialogOpen(true);
      },
    });
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    resetCadastre();
  };

  const renderSummaryRow = (label: string, value?: string | number | null) => {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    return (
      <Stack key={label} direction="row" spacing={1} justifyContent="space-between">
        <Typography variant="body2" color="text.secondary">
          {label}:
        </Typography>
        <Typography variant="body2" fontWeight={500} textAlign="right">
          {value}
        </Typography>
      </Stack>
    );
  };

  const renderCadastreData = (data: CadastrePropertySummary) => {
    const rawData: CadastreRawData = data.raw || {};

    return (
      <Stack spacing={2}>
        {renderSummaryRow('Referencia', data.reference)}
        {renderSummaryRow('Dirección', data.address)}
        {renderSummaryRow('Código Postal', data.postalCode)}
        {renderSummaryRow('Municipio', data.municipality)}
        {renderSummaryRow('Provincia', data.province)}
        {renderSummaryRow('Uso', data.usageDescription)}
        {renderSummaryRow(
          'Superficie construida',
          data.builtSurface ? `${data.builtSurface} m²` : undefined
        )}
        {renderSummaryRow(
          'Superficie parcela',
          data.plotSurface ? `${data.plotSurface} m²` : undefined
        )}
        {renderSummaryRow('Año construcción', data.constructionYear)}

        {renderSummaryRow('Planta', rawData.floor)}
        {renderSummaryRow('Puerta', rawData.door)}
        {renderSummaryRow('Escalera', rawData.staircase)}
        {renderSummaryRow('Bloque', rawData.block)}
        {renderSummaryRow('Número', rawData.number)}
        {renderSummaryRow('Tipo vía', rawData.streetType)}
        {renderSummaryRow('Nombre vía', rawData.streetName)}
      </Stack>
    );
  };

  const content = (
    <Stack spacing={3}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>

      <Stack direction="row" spacing={2} alignItems="flex-end">
        <TextField
          fullWidth
          label="Referencia Catastral"
          variant="outlined"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="Ej: 1234567CS1234S0001WX"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleFetchCadastreData();
            }
          }}
        />
        <Tooltip title="Consultar datos del Catastro">
          <span>
            <IconButton
              size="large"
              onClick={handleFetchCadastreData}
              disabled={isCadastralLoading || !reference.trim()}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&:disabled': {
                  bgcolor: 'grey.300',
                  color: 'grey.500',
                },
              }}
            >
              {isCadastralLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <TravelExploreIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Información del Catastro</DialogTitle>
        <DialogContent>
          {cadastralData && <Box sx={{ mt: 2 }}>{renderCadastreData(cadastralData)}</Box>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );

  if (!showAsCard) {
    return content;
  }

  return (
    <Card
      sx={{
        height: '100%',
        transition: 'all 0.3s ease-in-out',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
          borderColor: 'primary.main',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>{content}</CardContent>
    </Card>
  );
};

export default CadastreToolCard;
